import React from 'react';
import { observer } from 'mobx-react';
import { autorun, computed, observable } from 'mobx';
import { AppUI, LoadingState } from '@seedlang/state';
import styled from '@emotion/styled';
import MembershipOptionWhitelabel from 'components/membership/membership_option_whitelabel';
import { isPresent } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import Cookies from 'js-cookie';
import Alert from 'components/alert';
import { Theme } from '@seedlang/constants';
import { PaymentMigrationStore } from '@seedlang/stores';
import { Link } from 'react-router';
import Text from 'components/text';
import { SettingsBillingWrapper } from '@seedlang/hoc';
import Button from 'components/button/button';
import Stack from 'components/layout/stack';
import SegmentedControl from 'components/segmented_control';
import BannerNoticeIndex from 'components/banner_notice/banner_notice_index';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media only screen and (max-width : 760px) {
    width: 100%;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  margin-top: 30px;
`;

const OptionsGridWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 10px;
  margin-top: 20px;
`;

const MembershipOptionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  padding-bottom: 10px;
  width: 100%;
  max-width: 1080px;
  justify-content: center;
  
  @media only screen and (max-width: 730px) {
    padding: 0 20px;
  }
  
  @media only screen and (min-width: 731px) and (max-width: 1115px) {
    max-width: 800px;
  }
`;

const PatreonBanner = styled.div`
  height: 30px;
  background: #AFD6E7;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  text-align: center;
  font-size: 14px;
  line-height: 28px;
`;

const Footnote = styled.div`
  font-size: 12px;
  color: ${props => props.color};
  line-height: 15px;
  margin: 5px auto;
  text-align: center;
  overflow-wrap: break-word;
  max-width: 1080px;
  
  @media only screen and (max-width: 730px) {
    padding: 0 20px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FreeAccount = styled.div`
  display: flex;
  flex-direction: column;  
  align-items: center;
  background: #FFF;
  margin-top: 15px;
  width: 100%;
  max-width: 1080px;
  padding: 20px;
  border-radius: 5px;
  
  @media only screen and (max-width: 730px) {
    margin: 15px 20px 0;
    width: calc(100% - 40px);
  }
`;

const Title = styled.div`
  display: block;
  direction: auto;
  font-size: 22px;
  line-height: 22px;
  font-weight: bold;
  text-align: center;
  color: black;
  margin: 0;
  `;

const SegmentedControlContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto;
`;

@observer
class MembershipOptionsWhitelabel extends React.Component {
  @observable loadedMembershipGroup = false;
  @observable membershipsFilter = 1;
  @observable subscriptionLoaded = false;

  constructor(props) {
    super(props);
    if (!AppUI.userIsLoaded) {
      AppUI.loadUser();
    }
    AppUI.scrollToPageTop();
    autorun(() => {
      if (AppUI.userIsLoaded && AppUI.user.currentUserGroup?.membershipGroupId && !this.loadedMembershipGroup) {
        this.loadedMembershipGroup = true;
        this.loadMembershipGroup();
      }
      if (AppUI.userIsLoaded && AppUI.userSubscriptionStore.hasIndexData && !this.subscriptionLoaded) {
        this.subscriptionLoaded = true;
        if (isPresent(this.props.activeSubscription) && this.props.activeSubscription.membership?.numberMonths === 12) {
          this.onYearly();
        }
      }
    });
  }

  @autobind onMonthly() {
    this.membershipsFilter = 1;
  }

  @autobind onYearly() {
    this.membershipsFilter = 12;
  }

  @autobind onToggle() {
    this.membershipsFilter = this.membershipsFilter === 1 ? 12 : 1;
  }

  @autobind updateCurrency(currency) {
    if (this.currency === currency) return;
    AppUI.user.set('currency', currency);
    AppUI.authUserStore.update({ data: { currency } });
  }

  @computed get membershipOptionWidth() {
    if (AppUI.layout.viewportWidth <= 730 || this.filteredMemberships.length === 1) {
      return '100%';
    }
    if (AppUI.layout.viewportWidth <= 1115 || (this.filteredMemberships.length === 4)) {
      return 'calc(50% - 7.5px)';
    }
    return '350px';
  }

  @computed get isMultiRowLayout() {
    const numOptions = this.filteredMemberships.length;
    if (AppUI.layout.viewportWidth <= 730) {
      return numOptions > 1;
    } else if (AppUI.layout.viewportWidth <= 1115) {
      return numOptions > 2;
    } else {
      return numOptions > 3;
    }
  }

  @computed get siteId() {
    return Cookies.get('site_id');
  }

  @computed get currency() {
    return AppUI.user?.currency ?? 'USD';
  }

  @computed get currencySymbol() {
    return this.currency?.toUpperCase() === 'USD' ? '$' : '€';
  }

  @autobind loadMembershipGroup() {
    if (isPresent(AppUI.user.currentUserGroup)) {
      AppUI.membershipGroupStore.getShow({ ids: { membershipGroupId: AppUI.user.currentUserGroup.membershipGroupId } });
    }
  }

  @computed get filteredMemberships() {
    return isPresent(this.activeMemberships) ? this.activeMemberships.filter(item => this.membershipsFilter === item.numberMonths && item.active) : [];
  }

  @computed get activeMemberships() {
    const membershipGroup = AppUI.user?.currentUserGroup?.membershipGroupId ? AppUI.membershipGroupStore.showData : AppUI.user?.site?.membershipGroup;
    return isPresent(membershipGroup) ? membershipGroup.memberships.filter(item => item.active) : [];
  }

  @autobind async onClick(membership, loadingState = null) {
    if (AppUI.user.hasPermanentGroupMembership) {
      return;
    }
    AppUI.set('currentlyViewedMembership', membership);

    if (this.props.mode === 'payment_migration' || AppUI.isPaymentMigrationStartable) {
      loadingState = loadingState || new LoadingState();
      loadingState.started();
      try {
        await PaymentMigrationStore.updateMembershipPromise({
          membershipId: membership?.id,
          currency: this.currency,
        });
        loadingState.succeeded();
        AppUI.loadUser();
        AppUI.routeStore.routeToNamed('onboarding.migration');
      } catch {
        loadingState.failed('An error occurred while setting the membership.');
      }
      return;
    }
    const routeTo = window.location.href && window.location.href.match('settings') ? 'settings.memberships.create' : 'memberships.create';
    AppUI.routeStore.routeToNamed(routeTo, { membershipId: membership.id });
  }

  render() {
    return (
      <Wrapper>
        {
          !AppUI.user.anyAdmin && !AppUI.user.isMember && AppUI.site.patreonClientId &&
          <PatreonBanner>
            Already a member on Patreon? <Link to='onboarding.patreon' className='underline'>Sign In here</Link>
          </PatreonBanner>
        }
        {
          AppUI.user.hasPermanentGroupMembership &&
          <Alert
            textAlign='center'
            margin='20px 0 20px 0'
            background={Theme.green}
          >
            You have a free membership and will not be able to purchase a membership. Please contact us if you think there is an error.
          </Alert>
        }
        {
          AppUI.isPaymentMigrationStartable && this.props.mode !== 'payment_migration' &&
          <Alert
            textAlign='center'
            margin='20px 0 20px 0'
            background={Theme.green}
          >
            You are currently subscribed via Patreon. If you wish to change your membership, you will then be asked to migrate your payment to our platform.
          </Alert>
        }
        {
          this.props.mode === 'payment_migration' && AppUI.isPaymentMigrationStartable &&
          <Alert
            textAlign='center'
            margin='20px 0 20px 0'
            background={Theme.green}
          >
            You are currently migrating your payment method. Please select a membership level below. You will not be charged yet.
          </Alert>
        }
        <BannerNoticeIndex route='memberships' />
        <InnerWrapper>
          <Text heading={2} margin='0 auto 10px auto'>
            {this.props.activeSubscription?.membership ? 'My Membership' : 'Choose Your Membership'}
          </Text>
          <SegmentedControlContainer>
            <SegmentedControl
              options={[
                { value: 1, label: 'Monthly' },
                { value: 12, label: 'Yearly' },
              ]}
              activeOption={this.membershipsFilter}
              onChange={value => value === 1 ? this.onMonthly() : this.onYearly()}
            />
          </SegmentedControlContainer>
          <OptionsGridWrapper>
            <Column>
              <MembershipOptionWrapper>
                {
                  this.filteredMemberships && this.filteredMemberships.map(item => {
                    return (
                      <MembershipOptionWhitelabel
                        key={item.id}
                        currency={this.currency}
                        currencySymbol={this.currencySymbol}
                        membership={item}
                        onClick={this.onClick}
                        width={this.membershipOptionWidth}
                        mode={this.props.mode}
                        memberships={this.activeMemberships}
                        toggleYearly={this.onToggle}
                        activeSubscription={this.props.activeSubscription}
                        paymentMigration={AppUI.isPaymentMigrationStartable || AppUI.isPaymentMigrationInProgress ? AppUI.user.paymentMigration : null}
                        expandable={this.isMultiRowLayout}
                      />
                    );
                  })
                }
              </MembershipOptionWrapper>
              {
                !AppUI.userIsSignedIn || AppUI.user.guest && !AppUI.membershipGroupStore.hasOutstandingRequests &&
                <FreeAccount>
                  <Title>Create Free Account</Title>
                  <p>
                    Undecided? It's free to register and try out our membership perks.
                  </p>
                  <Link to='onboarding.register'>
                    <Button>
                      Register
                    </Button>
                  </Link>
                </FreeAccount>
              }
            </Column>
          </OptionsGridWrapper>
          <Stack gap='sm' alignItems='center'>
            <SegmentedControl
              options={[
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
              ]}
              activeOption={this.currency}
              onChange={value => this.updateCurrency(value)}
              small
            />

            <Footnote>
              Our membership platform is hosted by Seedlang, Inc. By signing up, you create a free user account with Seedlang (<a href='https://seedlang.com/terms' target='_blank' rel='noopener noreferrer'>Terms of Service</a>), which can also be used for any Easy Languages membership. If you choose a paid membership on this page, you'll enter into a paid subscription agreement with Easy Languages GmbH. You can also use an existing Easy Languages or Seedlang account.
            </Footnote>
          </Stack>
        </InnerWrapper>
      </Wrapper>
    );
  }
}

export default SettingsBillingWrapper(MembershipOptionsWhitelabel);